/**
 * @module utils/fieldSettingRename
 * @description field_setting / description 等 Record 子 key 迁移工具（被 customColumnConfigRename、QB linkage 复用）
 *
 * key 规则必须与 UrlSetting 写入一致：`${table_code || 't0'}_${column_name}`。
 * migrate 时除改 key 外还要更新条目内的 column_name / table_code，避免条目内容与 key 不一致。
 *
 * buildRecordSubKeyPatch：MERGE_QB_PATCH 对 Record 配置做「子 key 级」合并时，
 * 必须对删除的旧 key 显式传 `undefined`，否则 mergeConfigLayer 浅合并会保留 `t0_旧名`。
 */

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function fieldSettingSubKey(
  tableCode: string,
  columnName: string,
): string {
  return `${tableCode || 't0'}_${columnName}`;
}

/** 迁移单层 field_setting Record；无对应旧 key 时返回 undefined */
export function migrateFieldSettingRecord(
  record: Record<string, unknown> | undefined,
  oldColumnName: string,
  newColumnName: string,
  tableCode: string,
  tableId: string,
): Record<string, unknown> | undefined {
  if (!record || !oldColumnName || !newColumnName || oldColumnName === newColumnName) {
    return undefined;
  }
  const prevKey = fieldSettingSubKey(tableCode, oldColumnName);
  const nextKey = fieldSettingSubKey(tableCode, newColumnName);
  if (!(prevKey in record)) {
    return undefined;
  }
  const next = { ...record };
  const raw = next[prevKey];
  next[nextKey] = isPlainRecord(raw)
    ? {
        ...raw,
        column_name: newColumnName,
        table_code: tableCode || 't0',
        table_id: tableId,
      }
    : raw;
  delete next[prevKey];
  return next;
}

/**
 * 对比迁移前后 Record，产出 patch（含 `旧key: undefined` 表示删除）
 * 供 mergeConfigLayer + DEEP_MERGE_KEYS 使用，不能直接把完整 next Record 塞进 patch。
 */
export function buildRecordSubKeyPatch(
  prev: Record<string, unknown> | undefined,
  next: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!next) {
    return undefined;
  }
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(next)) {
    if (!prev || prev[key] !== value) {
      patch[key] = value;
    }
  }
  if (prev) {
    for (const key of Object.keys(prev)) {
      if (!(key in next)) {
        patch[key] = undefined;
      }
    }
  }
  return Object.keys(patch).length ? patch : undefined;
}

export function buildFieldSettingRenameLayerPatch(
  layer: Record<string, unknown>,
  oldColumnName: string,
  newColumnName: string,
  tableCode: string,
  tableId: string,
): { field_setting: Record<string, unknown> } | undefined {
  const prev = layer.field_setting as Record<string, unknown> | undefined;
  const next = migrateFieldSettingRecord(
    prev,
    oldColumnName,
    newColumnName,
    tableCode,
    tableId,
  );
  const subPatch = buildRecordSubKeyPatch(prev, next);
  return subPatch ? { field_setting: subPatch } : undefined;
}

/** 同步 DVA save_column_infos 中已持久化的 field_setting（devtools / 晚到 bridge 用） */
export function migrateSaveColumnInfosFieldSetting(
  saveColumnInfos: unknown,
  oldColumnName: string,
  newColumnName: string,
  tableCode: string,
  tableId: string,
): unknown {
  if (!saveColumnInfos || typeof saveColumnInfos !== 'object') {
    return saveColumnInfos;
  }
  const root = saveColumnInfos as Record<string, unknown>;
  const chartsCfgSave = root.chartsCfgSave as
    | Record<string, { customizeSetting?: Record<string, Record<string, unknown>> }>
    | undefined;
  const tableSlot = chartsCfgSave?.Table?.customizeSetting?.table;
  if (!tableSlot?.field_setting || typeof tableSlot.field_setting !== 'object') {
    return saveColumnInfos;
  }
  const migrated = migrateFieldSettingRecord(
    tableSlot.field_setting as Record<string, unknown>,
    oldColumnName,
    newColumnName,
    tableCode,
    tableId,
  );
  if (!migrated) {
    return saveColumnInfos;
  }
  return {
    ...root,
    chartsCfgSave: {
      ...chartsCfgSave,
      Table: {
        ...chartsCfgSave!.Table,
        customizeSetting: {
          ...chartsCfgSave!.Table!.customizeSetting,
          table: {
            ...tableSlot,
            field_setting: migrated,
          },
        },
      },
    },
  };
}
